/* istanbul ignore file */
/**
 * Created by rathompson on 7/22/22
 * File: WebTable
 * Project: MSP ©Palo Alto Networks
 * © Palo Alto Networks
 */

import { ConnectedTable, RowInstance, TableProvider, TableState } from '@panwds/react-table'
import Legend from './Legend'
import { useCallback, useMemo, VFC } from 'react'
import { ARRAY, OBJECT } from '../../../../../common/utils/Utils'
import WebTableCell from './cellRenderer/WebTableCell'
import { ACCESS_LEVELS } from '../../../../constants/IAMConstants'
import V5TableWrapper from '../../../../../common/components/table/V5TableWrapper'
import type { Row } from 'react-table'
import type { IAM_PermissionSet } from '../../../../../common/reducers/types/IAMReducer.types'
import { get } from 'lodash'

const WebTable: VFC<{
  data: IAM_PermissionSet[],
  tableInstanceRef: any,
  tableRef: any,
  tableState: TableState<IAM_PermissionSet>,
  viewOnly?: boolean
  expanded?: boolean
}> = ({ data, tableInstanceRef, tableRef, tableState, viewOnly = false, expanded: _expanded = false }) => {

  const onCheck = useCallback((row: RowInstance<IAM_PermissionSet>) => {
    const updateChildren = (subRows: Row<IAM_PermissionSet>[], isSelected: boolean) => {
      subRows.forEach((row: Row<any>) => {
        tableRef.current?.toggleRowSelected(row.id, isSelected)
        if (ARRAY.hasArray(row.subRows)) {
          updateChildren(row.subRows, isSelected)
        }
      })
    }
    const isSelected = !row.isSelected
    tableRef.current?.toggleRowSelected(row.id, isSelected)
    if (ARRAY.hasArray(row.subRows)) {
      updateChildren(row.subRows, isSelected)
    }
  }, [tableRef])

  const updateRow = useCallback((row, access_level) => {
    const updateChildren = (subRows: Row<IAM_PermissionSet>[], access_level: string) => {
      subRows.forEach((row: Row<IAM_PermissionSet>) => {
        tableRef.current?.setRowState([row.id], { access_level })
        if (ARRAY.hasArray(row.subRows)) {
          updateChildren(row.subRows, access_level)
        }
      })
    }
    tableRef.current?.setRowState(row.id, { access_level })
    if (ARRAY.hasArray(row.subRows)) {
      updateChildren(row.subRows, access_level)
    }
  }, [tableRef])

  const onPermissionToggle = useCallback((row) => {
    const getNext = (access_level: string, row: RowInstance<IAM_PermissionSet>) => {
      if (row.original.parent) {
        const parentRow: { state: { access_level: string } } = tableRef.current?.flatRows?.find((r: RowInstance<IAM_PermissionSet>) => r.original.name === row.original.parent)
        const parent_access_level = get(parentRow, 'state.access_level', ACCESS_LEVELS.NONE)
        switch (parent_access_level) {
          case ACCESS_LEVELS.WRITE:
            return ACCESS_LEVELS.WRITE
          case ACCESS_LEVELS.READ:
            switch (access_level) {
              case ACCESS_LEVELS.READ:
                return ACCESS_LEVELS.WRITE
              case ACCESS_LEVELS.WRITE:
              default:
                return ACCESS_LEVELS.READ
            }
          case ACCESS_LEVELS.NONE:
            switch (access_level) {
              case ACCESS_LEVELS.READ:
                return ACCESS_LEVELS.NONE
              case ACCESS_LEVELS.WRITE:
                return ACCESS_LEVELS.READ
              case ACCESS_LEVELS.NONE:
              default:
                return ACCESS_LEVELS.WRITE
            }
          default:
            return ACCESS_LEVELS.NONE
        }
      }

      switch (access_level) {
        case ACCESS_LEVELS.READ:
          return ACCESS_LEVELS.NONE
        case ACCESS_LEVELS.WRITE:
          return ACCESS_LEVELS.READ
        case ACCESS_LEVELS.NONE:
        default:
          return ACCESS_LEVELS.WRITE
      }
    }
    const access_level = getNext(row.state.access_level, row)
    updateRow(row, access_level)
  }, [tableRef, updateRow])

  const columns = useMemo(() => [
    {
      id: 'id',
      width: '100%',
      customExpansionToggle: true,
      customGroupCount: true,
      Cell: (props: { row: RowInstance<IAM_PermissionSet>, value: any }) => <WebTableCell {...{ onCheck, onPermissionToggle, tableRef, viewOnly, ...props }} />
    }
  ], [onCheck, onPermissionToggle, tableRef, viewOnly])

  const customGetSubRows = (row: IAM_PermissionSet | Row<IAM_PermissionSet>) => {
    if (ARRAY.hasArray((row as any)?.children)) {
      return (row as any)?.child_instances?.map((child: any) => ({ parent_row: row, ...child }))
    }
    return []
  }

  const customHook = (hooks: any) => {
    hooks.prepareRow.push((row: any) => {
      if (!row.state.access_level) {
        row.state.access_level = ACCESS_LEVELS.NONE
      }
    })
  }

  const overrideHooks = [
    customHook
  ]

  const expanded = useMemo(() => {

    const reducer = (object: any, item: any, index: number) => {
      const id = `${item.level ? `${item.level}.` : ''}${index}`
      object[id] = _expanded
      if (ARRAY.hasArray(item.child_instances)) {
        item.child_instances.map((item: any) => ({ ...item, level: id })).reduce(reducer, object)
      }
      return object
    }

    return data.reduce(reducer, {})
  }, [_expanded, data])

  const globalFilter = useCallback((rows, columnIds, searchQuery) => {
    if (!searchQuery) {
      return rows
    }

    const lowercaseQuery = searchQuery.toLowerCase()
    const rowMatches = (row: any) => Object.entries(row.original).some(([id, rowValue]) => {
      switch (id) {
        case 'id':
        case 'name':
        default:
          return typeof rowValue === 'string' && rowValue.toLowerCase().includes(lowercaseQuery)
      }
    }) || row.subRows.some(rowMatches)

    return rows.filter(rowMatches).map((row: any) => ({ ...row, subRows: row.subRows })) // https://github.com/TanStack/table/issues/2064
  }, [])

  return (
    <>
      <V5TableWrapper style={{ height: 'calc(100% - 40px)', overflow: 'auto' }}>
        <TableProvider
          tableInstanceRef={tableInstanceRef}
          columns={columns}
          data={data}
          getSubRows={customGetSubRows}
          initialState={(OBJECT.hasObject(tableState) ? tableState : { expanded }) as unknown as TableState<any>}
          overrideHooks={overrideHooks}
          globalFilter={globalFilter}
        >
          <div style={{ maxHeight: '335px', minHeight: '335px' }}>
            <ConnectedTable
              appearance="primary"
              showHeader={false}
              showFooter={false}
            />
          </div>
        </TableProvider>
      </V5TableWrapper>
      {!viewOnly && (<Legend/>)}
    </>
  )
}

export default WebTable
